// src/components/finance/TransactList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Filter, PlusCircle, ChevronRight, Calendar, 
  ArrowUpRight, ArrowDownRight, Receipt, BadgePercent, 
  BarChart2, DollarSign, Euro, Download, ChevronDown, X, Users,
  Building, Tag, ShoppingCart, Check, Copy
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import { 
  collection, doc, getDoc, query, where, orderBy, limit, 
  getDocs, startAfter, Timestamp 
} from 'firebase/firestore';
import { useCurrency } from '../../utils/currencyUtils';

const TRANSACTIONS_PER_PAGE = 20;

const TransactList = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeBusinessId, setActiveBusinessId] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [copiedTransactionId, setCopiedTransactionId] = useState('');
  
  // Reference data
  const [businesses, setBusinesses] = useState([]);
  const [counterpartyNames, setCounterpartyNames] = useState({});
  const { formatAmount } = useCurrency();
 
  const RandIcon = ({ size = 16, className = "" }) => (
    <div style={{ width: size, height: size }} className={`flex items-center justify-center ${className}`}>
      <span className="font-medium" style={{ fontSize: size * 0.75 }}>R</span>
    </div>
  );

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!currentUser) return;
      
      setIsLoading(true);
      
      try {
        // First fetch businesses to use in the filter
        const businessesQuery = query(
          collection(db, 'businesses'),
          where('createdBy', '==', currentUser.uid)
        );
        
        const businessSnapshot = await getDocs(businessesQuery);
        const businessData = [];
        businessSnapshot.forEach(doc => {
          businessData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setBusinesses(businessData);
        
        // Now fetch transactions
        let transactionsQuery;
        
        if (activeBusinessId === 'all') {
          transactionsQuery = query(
            collection(db, 'financialTransactions'),
            where('createdBy', '==', currentUser.uid),
            orderBy('date', 'desc'),
            limit(TRANSACTIONS_PER_PAGE)
          );
        } else {
          transactionsQuery = query(
            collection(db, 'financialTransactions'),
            where('createdBy', '==', currentUser.uid),
            where('businessId', '==', activeBusinessId),
            orderBy('date', 'desc'),
            limit(TRANSACTIONS_PER_PAGE)
          );
        }
        
        const querySnapshot = await getDocs(transactionsQuery);
        
        const transactionsData = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          transactionsData.push({
            id: doc.id,
            ...data,
            // For invoices, use issueDate as the primary date if date isn't present
            date: data.date?.toDate() || 
                  (data.type === 'invoice' && data.issueDate?.toDate()) || 
                  new Date(),
            dueDate: data.dueDate?.toDate() || null,
            issueDate: data.issueDate?.toDate() || null,
            createdAt: data.createdAt?.toDate() || new Date()
          });
        });
        
        setTransactions(transactionsData);
        setFilteredTransactions(transactionsData);
        
        console.log("Raw transactions from DB:", querySnapshot.docs.map(doc => ({
          id: doc.id,
          type: doc.data().type,
          ...doc.data()
        })));
        // Set the last visible document for pagination
        const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        setLastVisible(lastDoc);
        
        // Check if there are more documents
        setHasMore(querySnapshot.docs.length === TRANSACTIONS_PER_PAGE);
        
        // Fetch counterparty names
        await fetchCounterpartyNames(transactionsData);
        
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTransactions();
  }, [currentUser, activeBusinessId]);
  
  // Load more transactions
  const loadMoreTransactions = async () => {
    if (!hasMore || !lastVisible) return;
    
    setIsLoadingMore(true);
    
    try {
      let transactionsQuery;
      
      if (activeBusinessId === 'all') {
        transactionsQuery = query(
          collection(db, 'financialTransactions'),
          where('createdBy', '==', currentUser.uid),
          orderBy('date', 'desc'),
          startAfter(lastVisible),
          limit(TRANSACTIONS_PER_PAGE)
        );
      } else {
        transactionsQuery = query(
          collection(db, 'financialTransactions'),
          where('createdBy', '==', currentUser.uid),
          where('businessId', '==', activeBusinessId),
          orderBy('date', 'desc'),
          startAfter(lastVisible),
          limit(TRANSACTIONS_PER_PAGE)
        );
      }
      
      const querySnapshot = await getDocs(transactionsQuery);
      
      const newTransactions = [];
      querySnapshot.forEach(doc => {
        const data = doc.data();
        newTransactions.push({
          id: doc.id,
          ...data,
          date: data.date?.toDate() || new Date(),
          dueDate: data.dueDate?.toDate() || null,
          createdAt: data.createdAt?.toDate() || new Date()
        });
      });
      
      // Update state
      setTransactions(prev => [...prev, ...newTransactions]);
      
      // Fetch counterparty names
      await fetchCounterpartyNames(newTransactions);
      
      // Apply filters to new transactions
      applyFilters([...transactions, ...newTransactions]);
      
      // Set the last visible document for pagination
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(lastDoc);
      
      // Check if there are more documents
      setHasMore(querySnapshot.docs.length === TRANSACTIONS_PER_PAGE);
      
    } catch (error) {
      console.error('Error loading more transactions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };
  
  // Fetch counterparty names
  const fetchCounterpartyNames = async (transactionsToProcess) => {
    const counterpartyIds = {
      customer: new Set(),
      supplier: new Set()
    };
    
    // Collect unique counterparty IDs
    transactionsToProcess.forEach(transaction => {
      if (transaction.counterpartyId && transaction.counterpartyType) {
        counterpartyIds[transaction.counterpartyType].add(transaction.counterpartyId);
      }
    });
    
    // Fetch customer names
    if (counterpartyIds.customer.size > 0) {
      try {
        const customerPromises = Array.from(counterpartyIds.customer).map(async id => {
          try {
            // ✅ CORRECT - Use getDoc with document reference
            const customerRef = doc(db, 'customers', id);
            const customerSnap = await getDoc(customerRef);
            
            if (customerSnap.exists()) {
              const customerData = customerSnap.data();
              return {
                id,
                name: customerData.name || customerData.businessName || 
                      `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim() || 
                      'Unknown Customer'
              };
            }
            return { id, name: 'Unknown Customer' };
          } catch (error) {
            console.error(`Error fetching customer ${id}:`, error);
            return { id, name: 'Unknown Customer' };
          }
        });
        
        const customers = await Promise.all(customerPromises);
        const customerNames = {};
        customers.forEach(customer => {
          customerNames[customer.id] = customer.name;
        });
        
        setCounterpartyNames(prev => ({
          ...prev,
          ...customerNames
        }));
      } catch (error) {
        console.error('Error fetching customer names:', error);
      }
    }
    
    // Fetch supplier names
    if (counterpartyIds.supplier.size > 0) {
      try {
        const supplierPromises = Array.from(counterpartyIds.supplier).map(async id => {
          try {
            // ✅ CORRECT - Use getDoc with document reference
            const supplierRef = doc(db, 'suppliers', id);
            const supplierSnap = await getDoc(supplierRef);
            
            if (supplierSnap.exists()) {
              const supplierData = supplierSnap.data();
              return {
                id,
                name: supplierData.name || supplierData.businessName || 
                      `${supplierData.firstName || ''} ${supplierData.lastName || ''}`.trim() || 
                      'Unknown Supplier'
              };
            }
            return { id, name: 'Unknown Supplier' };
          } catch (error) {
            console.error(`Error fetching supplier ${id}:`, error);
            return { id, name: 'Unknown Supplier' };
          }
        });
        
        const suppliers = await Promise.all(supplierPromises);
        const supplierNames = {};
        suppliers.forEach(supplier => {
          supplierNames[supplier.id] = supplier.name;
        });
        
        setCounterpartyNames(prev => ({
          ...prev,
          ...supplierNames
        }));
      } catch (error) {
        console.error('Error fetching supplier names:', error);
      }
    }
  };
  
  // Apply filters
  const applyFilters = (transactionsToFilter = transactions) => {
    let filtered = [...transactionsToFilter];
    
    // Apply type filter
    if (activeType !== 'all') {
      filtered = filtered.filter(transaction => transaction.type === activeType);
    }
    
    // Apply status filter
    if (activeStatus !== 'all') {
      filtered = filtered.filter(transaction => transaction.status === activeStatus);
    }
    
    // Apply date range filter
    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      filtered = filtered.filter(transaction => 
        transaction.date >= startDate
      );
    }
    
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(transaction => 
        transaction.date <= endDate
      );
    }
    
    // Apply search term
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction => 
        transaction.id?.toLowerCase().includes(lowerCaseSearchTerm) ||
        transaction.description?.toLowerCase().includes(lowerCaseSearchTerm) ||
        transaction.documentNumber?.toLowerCase().includes(lowerCaseSearchTerm) ||
        transaction.notes?.toLowerCase().includes(lowerCaseSearchTerm) ||
        (counterpartyNames[transaction.counterpartyId] || '').toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    setFilteredTransactions(filtered);
 };
 
 // Handle search input change
 const handleSearchChange = (e) => {
   setSearchTerm(e.target.value);
 };

 const handleCopyTransactionId = async (transactionId) => {
   try {
     await navigator.clipboard.writeText(transactionId);
     setCopiedTransactionId(transactionId);
     setTimeout(() => setCopiedTransactionId(''), 1500);
   } catch (error) {
     console.error('Error copying transaction ID:', error);
   }
 };
 
 // Apply filters when filter values change
 useEffect(() => {
   applyFilters();
 }, [activeType, activeStatus, dateRange, searchTerm]);
 
 // Reset all filters
 const resetFilters = () => {
   setSearchTerm('');
   setActiveType('all');
   setActiveStatus('all');
   setDateRange({ start: '', end: '' });
 };
 
 // Format date
 const formatDate = (date) => {
   if (!date) return '';
   
   return new Date(date).toLocaleDateString('en-US', {
     year: 'numeric',
     month: 'short',
     day: 'numeric'
   });
 };
 
 // Format currency
 const formatCurrency = (amount, negative = false) => {
   if (amount === undefined || amount === null) return '';
   
   const value = negative ? -Math.abs(amount) : amount;
   
   return new Intl.NumberFormat('en-US', {
     style: 'currency',
     currency: 'USD',
     minimumFractionDigits: 2
   }).format(value);
 };
 
 // Get transaction icon
 const getTransactionIcon = (transaction) => {
   switch (transaction.type) {
     case 'simple':
       return transaction.amount >= 0 ? ArrowUpRight : ArrowDownRight;
     case 'invoice':
       return Receipt;
     case 'bill':
       return BadgePercent;
     case 'budget':
       return BarChart2;
     default:
       return DollarSign;
   }
 };
 
 // Get status chip styling
 const getStatusStyle = (status) => {
   switch (status) {
     case 'draft':
       return 'bg-gray-100 text-gray-800';
     case 'sent':
     case 'pending':
       return 'bg-blue-100 text-blue-800';
     case 'paid':
     case 'completed':
       return 'bg-green-100 text-green-800';
     case 'overdue':
       return 'bg-red-100 text-red-800';
     default:
       return 'bg-gray-100 text-gray-700';
   }
 };
 
 // Get business name by ID
 const getBusinessName = (businessId) => {
   const business = businesses.find(b => b.id === businessId);
   return business ? business.name : 'Unknown Business';
 };
 
 // Get counterparty name by ID
 const getCounterpartyName = (transaction) => {
   if (!transaction.counterpartyId || !transaction.counterpartyType) return '';
   
   return counterpartyNames[transaction.counterpartyId] || 
          (transaction.counterpartyType === 'customer' ? 'Customer' : 'Supplier');
 };

 return (
   <div>
     {/* Search and Actions Bar */}
     <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
       <div className="relative flex-1">
         <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
         <input
           type="text"
           className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
           placeholder="Search transactions..."
           value={searchTerm}
           onChange={handleSearchChange}
         />
       </div>
       
       <div className="flex gap-3">
         <button
           onClick={() => setShowFilters(!showFilters)}
           className={`px-4 py-2.5 rounded-lg border flex items-center ${
             showFilters ? 'border-[#190192] text-[#190192] bg-purple-50' : 'border-gray-200 text-gray-600'
           }`}
         >
           <Filter size={18} className="mr-2" />
           Filters
         </button>
         
         <button className="p-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
           <Download size={18} />
         </button>
         
         <Link
           to="/finance/new"
           className="px-4 py-2.5 rounded-lg bg-[#190192] text-white flex items-center hover:bg-[#2C0DB5]"
         >
           <PlusCircle size={18} className="mr-2" />
           New Transaction
         </Link>
       </div>
     </div>
     
     {/* Filters Panel */}
     {showFilters && (
       <div className="bg-white rounded-xl shadow-md p-6 mb-6">
         <div className="flex justify-between items-center mb-4">
           <h3 className="text-lg font-medium text-gray-900">Filters</h3>
           <button
             onClick={resetFilters}
             className="text-[#190192] hover:text-[#2C0DB5] text-sm"
           >
             Reset Filters
           </button>
         </div>
         
         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           {/* Business Filter */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               Business
             </label>
             <select
               value={activeBusinessId}
               onChange={(e) => setActiveBusinessId(e.target.value)}
               className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
             >
               <option value="all">All Businesses</option>
               {businesses.map(business => (
                 <option key={business.id} value={business.id}>{business.name}</option>
               ))}
             </select>
           </div>
           
           {/* Type Filter */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               Transaction Type
             </label>
             <select
               value={activeType}
               onChange={(e) => setActiveType(e.target.value)}
               className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
             >
               <option value="all">All Types</option>
               <option value="simple">Simple Transaction</option>
               <option value="invoice">Invoice</option>
               <option value="bill">Bill</option>
               <option value="budget">Budget</option>
             </select>
           </div>
           
           {/* Status Filter */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               Status
             </label>
             <select
               value={activeStatus}
               onChange={(e) => setActiveStatus(e.target.value)}
               className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
             >
               <option value="all">All Statuses</option>
               <option value="draft">Draft</option>
               <option value="sent">Sent</option>
               <option value="pending">Pending</option>
               <option value="paid">Paid</option>
               <option value="overdue">Overdue</option>
             </select>
           </div>
           
           {/* Date Range */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               Start Date
             </label>
             <input
               type="date"
               value={dateRange.start}
               onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
               className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               End Date
             </label>
             <input
               type="date"
               value={dateRange.end}
               onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
               className="block w-full rounded-lg border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
             />
           </div>
         </div>
       </div>
     )}

     {/* Loading State */}
     {isLoading ? (
       <div className="flex justify-center items-center h-64">
         <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
       </div>
     ) : (
       <>
         {/* Empty State */}
         {transactions.length === 0 ? (
           <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
             <DollarSign size={48} className="mx-auto text-gray-400 mb-4" />
             <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
             <p className="text-gray-500 mb-6 max-w-md mx-auto">
               You haven't recorded any financial transactions yet. Click the 'New Transaction' button to get started.
             </p>
             <Link
               to="/finance/new"
               className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-[#2C0DB5]"
             >
               <PlusCircle size={16} className="mr-2" />
               Record Your First Transaction
             </Link>
           </div>
         ) : filteredTransactions.length === 0 ? (
           <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
             <DollarSign size={48} className="mx-auto text-gray-400 mb-4" />
             <h3 className="text-lg font-medium text-gray-900 mb-2">No matching transactions</h3>
             <p className="text-gray-500 mb-6 max-w-md mx-auto">
               We couldn't find any transactions matching your search criteria. Try adjusting your search or filters.
             </p>
             <button
               onClick={resetFilters}
               className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
             >
               Clear Filters
             </button>
           </div>
         ) : (
           /* Transaction Row Cards */
           <div className="bg-white rounded-xl shadow-md overflow-hidden">
             <div className="divide-y divide-gray-200">
               {filteredTransactions.map((transaction) => {
                 const TransactionIcon = getTransactionIcon(transaction);

                 return (
                   <div
                     key={transaction.id}
                     className="px-4 py-4 sm:px-6 hover:bg-gray-50 transition-colors"
                   >
                     <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                       <div className="min-w-0 flex-1">
                         <div className="flex items-start justify-between gap-3">
                           <div className="flex items-start gap-3 min-w-0">
                           <div className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${
                             transaction.type === 'simple' && transaction.amount >= 0 ? 'bg-green-100 text-green-600' :
                             transaction.type === 'simple' ? 'bg-red-100 text-red-600' :
                             transaction.type === 'invoice' ? 'bg-blue-100 text-blue-600' :
                             transaction.type === 'bill' ? 'bg-amber-100 text-amber-600' :
                             'bg-purple-100 text-purple-600'
                           }`}>
                             <TransactionIcon size={16} />
                           </div>
                           <div className="min-w-0 flex-1">
                             <p className="text-sm font-medium text-gray-900 break-words">
                               {transaction.description || 'Untitled transaction'}
                             </p>
                             {(transaction.type === 'invoice' || transaction.type === 'bill') && (
                               <div className="mt-1 text-xs text-gray-500 flex items-center gap-1 min-w-0">
                                 {transaction.type === 'invoice' ? (
                                   <Users size={12} className="shrink-0" />
                                 ) : (
                                   <Building size={12} className="shrink-0" />
                                 )}
                                 <span className="truncate">{getCounterpartyName(transaction)}</span>
                               </div>
                             )}
                             {transaction.type === 'simple' && transaction.category && (
                               <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                                 <Tag size={12} className="shrink-0" />
                                 <span className="break-words">{transaction.category}</span>
                               </div>
                             )}
                           </div>
                           </div>
                           <div className="sm:hidden text-right shrink-0">
                             <p className={`text-sm font-semibold ${
                               transaction.type === 'simple' && transaction.amount < 0 ? 'text-red-600' : 'text-green-600'
                             }`}>
                               {transaction.type === 'simple'
                                 ? formatAmount(transaction.amount)
                                 : formatAmount(transaction.totalAmount || transaction.amount)}
                             </p>
                             {transaction.status && (
                               <span className={`mt-1 inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full ${getStatusStyle(transaction.status)}`}>
                                 {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                               </span>
                             )}
                           </div>
                         </div>
                       </div>

                       <div className="sm:hidden grid grid-cols-2 gap-2 text-xs">
                         <div className="rounded-md bg-gray-50 px-2 py-1.5">
                           <p className="uppercase tracking-wide text-gray-500">Date</p>
                           <p className="text-gray-800">{formatDate(transaction.date)}</p>
                           {(transaction.type === 'invoice' || transaction.type === 'bill') && transaction.dueDate && (
                             <p className="text-gray-500">Due {formatDate(transaction.dueDate)}</p>
                           )}
                         </div>
                         <div className="rounded-md bg-gray-50 px-2 py-1.5">
                           <p className="uppercase tracking-wide text-gray-500">Business</p>
                           <p className="text-gray-800 truncate">{getBusinessName(transaction.businessId)}</p>
                           <p className="text-gray-500 truncate">{transaction.documentNumber || transaction.reference || '-'}</p>
                         </div>
                       </div>

                       <div className="sm:hidden flex items-center justify-between">
                         <div className="flex items-center gap-2 min-w-0">
                           <p className="text-[11px] text-gray-500 truncate">ID: {transaction.id}</p>
                           <button
                             type="button"
                             onClick={() => handleCopyTransactionId(transaction.id)}
                             className="text-gray-500 hover:text-[#190192] shrink-0"
                             title="Copy transaction ID"
                           >
                             <Copy size={12} />
                           </button>
                           {copiedTransactionId === transaction.id && (
                             <span className="text-[11px] text-green-600 shrink-0">Copied</span>
                           )}
                         </div>
                         <Link
                           to={`/finance/duplicate/${transaction.id}`}
                           className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center text-sm font-medium shrink-0 ml-2"
                         >
                           <Copy size={14} className="mr-1" /> Duplicate
                         </Link>
                         <Link
                           to={`/finance/${transaction.id}`}
                           className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center text-sm font-medium shrink-0"
                         >
                           View <ChevronRight size={14} className="ml-1" />
                         </Link>
                       </div>

                       <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 lg:w-auto w-full">
                         <div className="min-w-0">
                           <p className="text-[11px] uppercase tracking-wide text-gray-500">Date</p>
                           <p className="text-sm text-gray-900">{formatDate(transaction.date)}</p>
                           {(transaction.type === 'invoice' || transaction.type === 'bill') && transaction.dueDate && (
                             <p className="text-xs text-gray-500">Due {formatDate(transaction.dueDate)}</p>
                           )}
                         </div>

                         <div className="min-w-0">
                           <p className="text-[11px] uppercase tracking-wide text-gray-500">Business</p>
                           <p className="text-sm text-gray-700 break-words">{getBusinessName(transaction.businessId)}</p>
                           <p className="text-xs text-gray-500 break-all">
                             {transaction.documentNumber || transaction.reference || '-'}
                           </p>
                           <div className="mt-1 flex items-center gap-2">
                             <p className="text-[11px] text-gray-500 break-all">ID: {transaction.id}</p>
                             <button
                               type="button"
                               onClick={() => handleCopyTransactionId(transaction.id)}
                               className="text-gray-500 hover:text-[#190192]"
                               title="Copy transaction ID"
                             >
                               <Copy size={12} />
                             </button>
                             {copiedTransactionId === transaction.id && (
                               <span className="text-[11px] text-green-600">Copied</span>
                             )}
                           </div>
                         </div>

                         <div className="min-w-0 lg:text-right">
                           <p className="text-[11px] uppercase tracking-wide text-gray-500">Amount</p>
                           <p className={`text-sm font-semibold ${
                             transaction.type === 'simple' && transaction.amount < 0 ? 'text-red-600' :
                             'text-green-600'
                           }`}>
                             {transaction.type === 'simple'
                              ? formatAmount(transaction.amount)
                              : formatAmount(transaction.totalAmount || transaction.amount)}
                           </p>
                           {(transaction.type === 'invoice' || transaction.type === 'bill') && transaction.balanceDue > 0 && (
                             <p className="text-xs text-gray-500">{formatAmount(transaction.balanceDue)} due</p>
                           )}
                         </div>

                         <div className="flex flex-col sm:items-start lg:items-end gap-2">
                           <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                             {transaction.status && (
                               <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusStyle(transaction.status)}`}>
                                 {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                               </span>
                             )}
                             {transaction.isRecurring && (
                               <span className="text-xs text-purple-600 inline-flex items-center">
                                 <Check size={10} className="mr-1" />
                                 Recurring
                               </span>
                             )}
                           </div>
                           <Link
                             to={`/finance/duplicate/${transaction.id}`}
                             className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center text-sm font-medium"
                           >
                             <Copy size={16} className="mr-1" /> Duplicate
                           </Link>
                           <Link
                             to={`/finance/${transaction.id}`}
                             className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center text-sm font-medium"
                           >
                             View <ChevronRight size={16} className="ml-1" />
                           </Link>
                         </div>
                       </div>
                     </div>
                   </div>
                 );
               })}
             </div>
             
             {/* Load More Button */}
             {hasMore && (
               <div className="px-6 py-4 border-t border-gray-200 text-center">
                 <button
                   onClick={loadMoreTransactions}
                   disabled={isLoadingMore}
                   className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                 >
                   {isLoadingMore ? (
                     <>
                       <span className="inline-block animate-spin h-4 w-4 border-2 border-gray-500 rounded-full border-t-transparent mr-2 align-middle"></span>
                       Loading...
                     </>
                   ) : (
                     'Load More'
                   )}
                 </button>
               </div>
             )}
           </div>
         )}
       </>
     )}
   </div>
 );
};

export default TransactList;
